import api from 'service/api'

// One-time Kehila -> Protocols migration wizard. Talks directly to the Protocols WebAPI
// (KehilaMigrationController) using the same baseUrl/authToken the Protocols module already
// obtains via modulesStore.openProtocolModule (Mekome -> Protocols token exchange).
// This whole feature is temporary and will be removed once the migration is complete.

export type KehilaConnectionTestResponse = {
  categoriesCount: number
  protocolsCount: number
}

export type KehilaCategoryResponse = {
  oldCategoryId: number
  categoryName: string
}

export type KehilaCategorySelection = {
  oldCategoryId: number
  categoryName: string
}

export type KehilaCreateCategoriesResponse = {
  created: number
  reused: number
}

export type KehilaImportStartResponse = {
  alreadyRunning: boolean
}

export type KehilaCategoryProgress = {
  oldCategoryId: number
  categoryName: string
  done: number
  total: number
}

export type KehilaLogEntry = {
  id: number
  categoryOldId: number | null
  protocolOldId: number | null
  status: string
  statusDetails: string | null
  createdDate: string
}

export type KehilaImportStatusResponse = {
  isRunning: boolean
  total: number
  done: number
  imported: number
  skipped: number
  failed: number
  categories: KehilaCategoryProgress[]
  recentEntries: KehilaLogEntry[]
}

const authHeader = (authToken: string) => ({ Authorization: `Bearer ${authToken}` })

const isErrorResponse = (res: any) => !!res?.status && res.status >= 400

// ExceptionHandlerMiddleware on the Protocols side returns { status, message } for domain
// exceptions (BadRequest/NotFound/Conflict/etc.) - newProtocolFetcher surfaces that body as
// res.data. This is an admin-only diagnostic tool, so the real backend message should reach the UI
// instead of a generic fallback.
const extractErrorMessage = (res: any): string =>
  res?.data?.message || res?.errorDescription || res?.error || 'The request failed.'

export type KehilaConnectionTestResult =
  | { success: true; data: KehilaConnectionTestResponse }
  | { success: false; message: string }

export const testKehilaConnection = async (
  baseUrl: string, authToken: string, communityId: number
): Promise<KehilaConnectionTestResult> => {
  const res = await api.newProtocolFetcher(
    `${baseUrl}api/kehila-migration/test-connection`, 'POST', { communityId }, authHeader(authToken)
  )
  if (isErrorResponse(res)) {
    return { success: false, message: extractErrorMessage(res) }
  }
  return { success: true, data: res }
}

export const getKehilaCategories = async (
  baseUrl: string, authToken: string, communityId: number
): Promise<KehilaCategoryResponse[]> => {
  const res = await api.newProtocolFetcher(
    `${baseUrl}api/kehila-migration/categories`, 'GET', { communityId }, authHeader(authToken)
  )
  return isErrorResponse(res) ? [] : (res || [])
}

export const createKehilaCategories = async (
  baseUrl: string, authToken: string, kehilaCommunityId: number, mekomeCommunityId: number, categories: KehilaCategorySelection[]
): Promise<KehilaCreateCategoriesResponse | null> => {
  const res = await api.newProtocolFetcher(
    `${baseUrl}api/kehila-migration/categories`, 'POST', { kehilaCommunityId, mekomeCommunityId, categories }, authHeader(authToken)
  )
  return isErrorResponse(res) ? null : res
}

export const startKehilaImport = async (
  baseUrl: string, authToken: string, kehilaCommunityId: number, mekomeCommunityId: number
): Promise<KehilaImportStartResponse | null> => {
  const res = await api.newProtocolFetcher(
    `${baseUrl}api/kehila-migration/protocols/import`, 'POST', { kehilaCommunityId, mekomeCommunityId }, authHeader(authToken)
  )
  return isErrorResponse(res) ? null : res
}

export const retryKehilaImport = async (
  baseUrl: string, authToken: string, kehilaCommunityId: number, mekomeCommunityId: number
): Promise<KehilaImportStartResponse | null> => {
  const res = await api.newProtocolFetcher(
    `${baseUrl}api/kehila-migration/protocols/import/retry-failed`, 'POST', { kehilaCommunityId, mekomeCommunityId }, authHeader(authToken)
  )
  return isErrorResponse(res) ? null : res
}

export const getKehilaImportStatus = async (
  baseUrl: string, authToken: string, kehilaCommunityId: number, mekomeCommunityId: number
): Promise<KehilaImportStatusResponse | null> => {
  const res = await api.newProtocolFetcher(
    `${baseUrl}api/kehila-migration/protocols/import/status`, 'GET', { kehilaCommunityId, mekomeCommunityId }, authHeader(authToken)
  )
  return isErrorResponse(res) ? null : res
}

export const downloadKehilaLog = async (baseUrl: string, authToken: string, mekomeCommunityId: number): Promise<void> => {
  try {
    const response = await api.secure.get(`${baseUrl}api/kehila-migration/protocols/import/log/download`, {
      params: { mekomeCommunityId },
      headers: authHeader(authToken),
      responseType: 'blob',
      withCredentials: true,
    })
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `kehila-migration-log-${mekomeCommunityId}.csv`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (e) {
    console.error('Failed to download Kehila migration log', e)
  }
}
